'use client';

import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Pagination } from '@/components/ui/Pagination';
import { usePriceConvert } from '@/lib/PriceContext';
import { useT } from '@/hooks/useT';
import { gameCount as fmtGameCount } from '@/lib/i18n';
import { CURRENCY_LOCALE, currencyForLang } from '@/lib/currency';
import Link from 'next/link';
import { ChevronDown, Shield, LayoutGrid, Sparkles, Zap, SlidersHorizontal, X } from 'lucide-react';
import type { NewGameItem } from '@/lib/api';
import { GENRES } from '@/components/catalog/GenreFilter';
import { genreLabel } from '@/lib/genreLabel';
import { LanguageFilter, ALL_LANG_TYPES, LANG_LIST, langName, type LangType } from '@/components/catalog/LanguageFilter';
import { OfferFilter, readOfferParams, offerLinkSuffix, OFFER_MULTS, OFFER_ICONS, ALL_REGIONS, type OfferType, type RegionCode } from '@/components/catalog/OfferFilter';
import { fetchPrices } from '@/lib/priceCache';
import { useSearchParams } from 'next/navigation';

const PAGE_SIZE = 30;

const PLATFORM_MAP: Record<string, { label: string; cls: string; svg?: boolean }> = {
  XBOXSERIESX: { label: 'X|S',    cls: 'ptag-xsx' },
  XBOXONE:     { label: 'ONE',    cls: 'ptag-xone' },
  PC:          { label: 'PC',     cls: 'ptag-pc' },
  XCLOUD:      { label: 'xCloud', cls: 'ptag-cloud', svg: true },
};
// Фільтр платформ — як у каталозі.
type PlatKey = 'all' | 'xsx' | 'xone' | 'pc' | 'xcloud' | 'handheld';
const PLAT_KEYS: PlatKey[] = ['all', 'xsx', 'xone', 'pc', 'xcloud', 'handheld'];
const PLAT_VALUE: Record<PlatKey, string> = { all: '', xsx: 'XBOXSERIESX', xone: 'XBOXONE', pc: 'PC', xcloud: '', handheld: '' };
const PLAT_CHIP: Record<PlatKey, string> = { all: 'on', xsx: 'on-xsx', xone: 'on-xone', pc: 'on-pc', xcloud: 'on-xcloud', handheld: 'on-handheld' };
const PLAT_LABEL: Record<PlatKey, string> = { all: '', xsx: 'Series X|S', xone: 'Xbox One', pc: 'PC', xcloud: 'xCloud', handheld: 'Handheld' };

const CloudIcon = () => (
  <svg viewBox="0 0 32 22" width="13" height="9" fill="currentColor" aria-label="Xbox Cloud Gaming" style={{ display: 'block' }}>
    <path d="M26 9a6 6 0 0 0-1-.08A8 8 0 0 0 10.1 7.4 5 5 0 1 0 5 17h21a5 5 0 1 0 0-10c-.34 0-.68.03-1 .08A5.99 5.99 0 0 0 26 9z"/>
  </svg>
);

// Детермінований формат числа (пробіл-роздільник) — узгоджено з CatalogShell.
function fmtCount(n: number): string {
  return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

// Локалізована назва країни за ISO-кодом регіону (AR→Аргентина за мовою сайту).
function regionName(code: string, lang: string): string {
  try {
    const loc = lang === 'ru' ? 'ru-RU' : lang === 'uk' ? 'uk-UA' : 'en';
    return new Intl.DisplayNames([loc], { type: 'region' }).of(code) ?? code;
  } catch { return code; }
}

function fmtPrice(price: number, currency: string | null): string {
  return new Intl.NumberFormat('uk-UA', {
    style: 'currency', currency: currency || 'USD', maximumFractionDigits: 2,
  }).format(price);
}

function fmtReleaseDate(dateStr: string | null, year: number | null, lang: string): string | null {
  if (!dateStr) return year ? String(year) : null;
  const d = new Date(dateStr);
  if (d.getFullYear() >= 2100) return null;
  const diffDays = (Date.now() - d.getTime()) / 86400000;
  // Той самий мапінг мова→locale, що вже використовується для конвертації цін
  // (CURRENCY_LOCALE) — раніше тут завжди був жорстко закодований 'uk-UA',
  // тому дата на картці не оновлювалась при зміні мови інтерфейсу.
  const locale = CURRENCY_LOCALE[currencyForLang(lang)] ?? 'en-US';
  if (diffDays < 90) return d.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
  return String(d.getFullYear());
}

type CardProps = { item: NewGameItem; idx: number; offerType?: OfferType; region?: RegionCode; priceOverride?: number | null };

// Спільні похідні дані картки (ціна/платформи/дата/знижка/тип/рейтинг).
function useNgCardData({ item, offerType = 'key', priceOverride }: CardProps) {
  const { convert: fmtConverted, lang } = usePriceConvert();
  const minOfferUsd =
    offerType === 'gift'    ? item.min_gift_usd
    : offerType === 'account' ? item.min_account_usd
    : offerType === 'rental'  ? item.min_rental_usd
    : item.min_key_usd;
  const basePrice = priceOverride !== undefined
    ? priceOverride
    : minOfferUsd ?? ((item.list_price ?? null) != null ? (item.list_price! * OFFER_MULTS[offerType]) : null);
  const platforms = item.platforms.map(p => PLATFORM_MAP[p]).filter(Boolean) as { label: string; cls: string; svg?: boolean }[];
  const releaseLabel = fmtReleaseDate(item.release_at, item.release_year, lang);
  const discount = item.discount_pct && item.discount_pct > 0 ? Math.round(item.discount_pct) : null;
  const isAddon = item.product_type === 'Durable' || item.product_type === 'Consumable';
  const rating = item.rating ?? 0;
  const priceStr = basePrice != null && basePrice > 0 ? (fmtConverted(basePrice) ?? fmtPrice(basePrice, 'USD')) : null;
  return { basePrice, priceStr, platforms, releaseLabel, discount, isAddon, rating, lang };
}

const Platforms = ({ platforms, cls }: { platforms: { label: string; cls: string; svg?: boolean }[]; cls: string }) => (
  <div className={cls}>
    {platforms.map(p => (
      <span key={p.label} className={`platform-tag ${p.cls}`}>{p.svg ? <CloudIcon /> : p.label}</span>
    ))}
  </div>
);

// Варіант A — постер-картка, як на /sale (вертикальний постер, інфо знизу).
function NgPosterCard(props: CardProps) {
  const { item, idx } = props;
  const { priceStr, platforms, releaseLabel, discount, isAddon, rating, lang } = useNgCardData(props);
  const img = item.poster ?? item.hero;
  const hrefSuffix = offerLinkSuffix(props.offerType ?? 'key', props.region ?? 'any');
  return (
    <Link href={`/product/${item.product_id}${hrefSuffix}`} className="cat-card" style={{ textDecoration: 'none' }}>
      <div className="cat-art-wrap">
        <div className="cat-art-bg">
          {img ? <img src={img} alt={item.title} loading={idx < 10 ? 'eager' : 'lazy'} /> : <div style={{ width: '100%', height: '100%', background: '#1a1a22' }} />}
        </div>
        <span className={`badge-pill badge-type ${isAddon ? 'badge-dlc' : 'badge-game'}`}>{isAddon ? 'DLC' : 'GAME'}</span>
        {discount && <span className="badge-pill badge-disc">−{discount}%</span>}
        {releaseLabel && <span className="ngp-date">{releaseLabel}</span>}
        {platforms.length > 0 && <Platforms platforms={platforms} cls="cat-platforms" />}
      </div>
      <div className="cat-info ngp-info">
        <div className="ngp-info-main">
        <div className="cat-name" title={item.title}>{item.title}</div>
        <div className="cat-genre">{item.category ? genreLabel(item.category, lang) : ' '}</div>
        </div>
        <div className="ngp-info-side">
          {priceStr && <span className="cat-price">{priceStr}</span>}
          {rating > 0 && (
            <div className="cat-rating"><span style={{ color: '#f59e0b' }}>★</span><span>{rating.toFixed(1)}</span></div>
          )}
        </div>
      </div>
    </Link>
  );
}

type TypeFilter = 'all' | 'game' | 'dlc';

export default function ReleasesShell({ items, totalCount }: { items: NewGameItem[]; totalCount?: number }) {
  const t = useT();

  // items/total — з /api/releases-page (SQL WHERE+LIMIT/OFFSET). SSR-дані одразу
  // коректні для дефолтного вигляду (releases не читає фільтри з URL).
  const [pageItems, setPageItems] = useState<NewGameItem[]>(items);
  const [total,     setTotal]     = useState(totalCount ?? items.length);
  const [loading,   setLoading]   = useState(false);

  const { lang } = usePriceConvert();
  const [typeFilter,   setTypeFilter]   = useState<TypeFilter>('all');
  const [plat,         setPlat]         = useState<PlatKey>('all');
  const [xsxOnly,      setXsxOnly]      = useState(false);
  const [goldRequired, setGoldRequired] = useState(false);
  const [genre,        setGenre]        = useState('');
  const [genreRowOpen, setGenreRowOpen] = useState(false);
  const [sheetOpen,    setSheetOpen]    = useState(false);
  const [openSec,      setOpenSec]      = useState<Set<string>>(() => new Set(['type']));
  const toggleSec = (id: string) => setOpenSec(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const Sec = (id: string, label: string, badge: number, content: React.ReactNode) => (
    <div className={`fbar-acc${openSec.has(id) ? ' open' : ''}`}>
      <button className="fbar-acc-head" onClick={() => toggleSec(id)}>
        <span className="fbar-acc-title">{label}{badge > 0 && <span className="fbar-acc-badge">{badge}</span>}</span>
        <ChevronDown size={18} className="fbar-acc-chev" />
      </button>
      <div className="fbar-acc-body"><div className="fbar-acc-inner">{content}</div></div>
    </div>
  );
  const [sort] = useState('date_desc');
  const [langFilter,   setLangFilter]   = useState<string[]>([]);
  const [langTypes,    setLangTypes]    = useState<LangType[]>(ALL_LANG_TYPES);
  const searchParams = useSearchParams();
  const [offerType, setOfferType] = useState<OfferType>(() => readOfferParams(searchParams).offerType);
  const [region,    setRegion]    = useState<RegionCode>(() => readOfferParams(searchParams).region);
  const [priceMap,  setPriceMap]  = useState<Record<string, number>>({});
  const [page, setPage] = useState(1);
  const gridRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (region === 'any') { setPriceMap({}); return; }
    setPriceMap({});  // clear stale prices immediately
    fetchPrices(offerType, region).then(setPriceMap);
  }, [offerType, region]);

  function goToPage(n: number) {
    setPage(n);
    gridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // Головне джерело даних — /api/releases-page. Дебаунс 250мс. Перший рендер
  // (дефолтні фільтри, page=1) пропускає фетч — SSR-дані вже точні.
  const isFirstRun = useRef(true);
  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    const controller = new AbortController();
    const timer = setTimeout(() => {
      const p = new URLSearchParams();
      if (typeFilter !== 'all') p.set('type', typeFilter);
      if (plat !== 'all')       p.set('plat', plat);
      if (xsxOnly)      p.set('xsx', '1');
      if (goldRequired) p.set('gold', '1');
      if (genre)        p.set('genre', genre);
      if (langFilter.length && langTypes.length) {
        p.set('langs', langFilter.join(','));
        p.set('langTypes', langTypes.join(','));
      }
      if (page > 1) p.set('page', String(page));

      setLoading(true);
      fetch(`/api/releases-page?${p.toString()}`, { signal: controller.signal, credentials: 'same-origin' })
        .then(r => r.json())
        .then(data => { setPageItems(data.items ?? []); setTotal(data.total ?? 0); })
        .catch(e => { if ((e as Error).name !== 'AbortError') console.error('[releases] fetch failed:', e); })
        .finally(() => setLoading(false));
    }, 250);
    return () => { clearTimeout(timer); controller.abort('filters changed'); };
  }, [typeFilter, plat, xsxOnly, goldRequired, genre, langFilter, langTypes, page]); // eslint-disable-line react-hooks/exhaustive-deps

  const availableCount = total;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const safePage   = page;
  const visible    = pageItems;
  const filtered   = pageItems;

  const chip = (active: boolean) => active ? 'va-chip on' : 'va-chip';
  const platChip = (p: PlatKey, active: boolean) => active ? `va-chip ${PLAT_CHIP[p]}` : 'va-chip';

  const activeCount = [
    typeFilter !== 'all', plat !== 'all', xsxOnly, goldRequired, genre !== '',
    langFilter.length > 0, offerType !== 'key', region !== 'any',
  ].filter(Boolean).length;

  function resetAll() {
    setTypeFilter('all'); setPlat('all'); setXsxOnly(false); setGoldRequired(false);
    setGenre(''); setLangFilter([]); setLangTypes(ALL_LANG_TYPES);
    setOfferType('key'); setRegion('any');
    setPage(1);
  }

  return (
    <>
      {/* Reactive header */}
      <div className="ngv2-header">
        <div className="ngv2-header-inner">
          <span className="ngv2-header-ico"><Sparkles size={22} strokeWidth={2.2} /></span>
          <div className="ngv2-header-text">
            <h1 className="ngv2-title-h1">{t('releases_title')}</h1>
            <p className="ngv2-sub">{t('releases_desc')} · {fmtGameCount(availableCount, lang)}</p>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1560, margin: '0 auto', padding: '11px 24px 0' }}>
        {/* -- Mobile filter trigger (bottom-sheet) -- */}
        <div className="fbar-mobile">
          <button className={`fbar-m-btn${activeCount > 0 ? ' on' : ''}`} onClick={() => setSheetOpen(true)}>
            <SlidersHorizontal size={15} /> {t('m_filters')}
            {activeCount > 0 && <span className="fbar-m-badge">{activeCount}</span>}
          </button>
          <span className="fbar-m-count">{fmtCount(total)}</span>
        </div>

        <div className="po2-filters po2-filters-1row">
          {/* Type chips — ідентично каталогу */}
          <div className="po2-platform-chips">
            <span className="po2-flabel">{t('filter_type')}</span>
            <button className={chip(typeFilter === 'all')} onClick={() => setTypeFilter('all')}>
              {t('catalog_type_all')}
            </button>
            <button className={chip(typeFilter === 'game')} onClick={() => setTypeFilter('game')}>
              {t('catalog_type_game')}
            </button>
            <button className={chip(typeFilter === 'dlc')} onClick={() => setTypeFilter('dlc')}>
              {t('catalog_type_dlc')}
            </button>
          </div>

          <div className="po2-sep" />

          {/* Platform filter (як у каталозі) */}
          <div className="po2-platform-chips">
            <span className="po2-flabel">{t('filter_platform')}</span>
            {PLAT_KEYS.map(p => (
              <button key={p} className={platChip(p, plat === p)} onClick={() => { setPlat(p); setPage(1); }}>
                {p === 'all' ? t('platform_all') : PLAT_LABEL[p]}
              </button>
            ))}
            <button className={chip(xsxOnly)} onClick={() => { setXsxOnly(v => !v); setPage(1); }} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Zap size={11} />{t('catalog_optimized')}
            </button>
          </div>

          <div className="po2-sep" />

          <LanguageFilter
            langs={langFilter}
            types={langTypes}
            onChange={(l, tk) => { setLangFilter(l); setLangTypes(tk); setPage(1); }}
          />
          <OfferFilter basePath="/releases" offerType={offerType} region={region} onOfferChange={(o, r) => { setOfferType(o); setRegion(r); setPage(1); }} />
          <button className={`va-chip${goldRequired ? ' on-gold' : ''}`} onClick={() => { setGoldRequired(n => !n); setPage(1); }} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Shield size={11} />{t('filter_gold')}
          </button>

          {/* Genre toggle */}
          <button
            className={`va-chip${genreRowOpen || genre !== '' ? ' on' : ''}`}
            onClick={() => setGenreRowOpen(o => !o)}
            style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0, marginLeft: 'auto' }}
          >
            {t('filter_genres')}
            <ChevronDown size={10} style={{ transition: 'transform .2s ease', transform: genreRowOpen ? 'rotate(180deg)' : 'rotate(0deg)' }} />
          </button>
        </div>

        {/* Expandable genre row */}
        <div className={`fbar-genre-expand${genreRowOpen ? ' open' : ''}`}>
          <div className="fbar-row fbar-genre-row-inner">
            <button
              className={chip(genre === '')}
              onClick={() => setGenre('')}
              style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}
            >
              <LayoutGrid size={11} strokeWidth={2} />
              {t('filter_all_genres')}
            </button>
            {GENRES.map(({ key, i18nKey, Icon }) => (
              <button
                key={key}
                className={chip(genre === key)}
                onClick={() => setGenre(genre === key ? '' : key)}
                style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}
              >
                <Icon size={11} strokeWidth={2} />
                {t(i18nKey as never)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* -- Mobile filter bottom-sheet -- */}
      <div className={`fbar-scrim${sheetOpen ? ' open' : ''}`} onClick={() => setSheetOpen(false)} />
      <div className={`fbar-sheet${sheetOpen ? ' open' : ''}`} role="dialog" aria-modal="true" aria-label={t('m_filters')}>
        <div className="fbar-sheet-grip" />
        <div className="fbar-sheet-head">
          <span className="fbar-sheet-title">{t('m_filters')}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {activeCount > 0 && <button className="fbar-sheet-reset" onClick={resetAll}>{t('m_reset')} ({activeCount})</button>}
            <button className="fbar-sheet-x" aria-label="✕" onClick={() => setSheetOpen(false)}><X size={16} /></button>
          </div>
        </div>
        <div className="fbar-sheet-body">
          {/* Quick on/off filters (booleans) */}
          <div className="fbar-quick fbar-group-chips">
            <button className={chip(xsxOnly)} onClick={() => { setXsxOnly(v => !v); setPage(1); }} style={{ display: 'flex', alignItems: 'center', gap: 5 }}><Zap size={13} />{t('catalog_optimized')}</button>
            <button className={`va-chip${goldRequired ? ' on-gold' : ''}`} onClick={() => { setGoldRequired(v => !v); setPage(1); }} style={{ display: 'flex', alignItems: 'center', gap: 5 }}><Shield size={13} />{t('filter_gold')}</button>
          </div>
          {Sec('type', t('filter_type'), typeFilter !== 'all' ? 1 : 0, (
            <div className="fbar-group-chips">
              <button className={chip(typeFilter === 'all')} onClick={() => { setTypeFilter('all'); setPage(1); }}>{t('catalog_type_all')}</button>
              <button className={chip(typeFilter === 'game')} onClick={() => { setTypeFilter('game'); setPage(1); }}>{t('catalog_type_game')}</button>
              <button className={chip(typeFilter === 'dlc')} onClick={() => { setTypeFilter('dlc'); setPage(1); }}>{t('catalog_type_dlc')}</button>
            </div>
          ))}
          {Sec('platform', t('filter_platform'), plat !== 'all' ? 1 : 0, (
            <div className="fbar-group-chips">
              {PLAT_KEYS.map(p => (
                <button key={p} className={platChip(p, plat === p)} onClick={() => { setPlat(p); setPage(1); }}>
                  {p === 'all' ? t('platform_all') : PLAT_LABEL[p]}
                </button>
              ))}
            </div>
          ))}
          {Sec('genre', t('filter_genres'), genre !== '' ? 1 : 0, (
            <div className="fbar-group-chips">
              <button className={chip(genre === '')} onClick={() => setGenre('')} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <LayoutGrid size={12} /> {t('filter_all_genres')}
              </button>
              {GENRES.map(({ key, i18nKey, Icon }) => (
                <button key={key} className={chip(genre === key)} onClick={() => setGenre(genre === key ? '' : key)} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <Icon size={12} /> {t(i18nKey as never)}
                </button>
              ))}
            </div>
          ))}
          {Sec('offer', t('m_offer'), offerType !== 'key' ? 1 : 0, (
            <div className="fbar-group-chips">
              {(['key', 'gift', 'account', 'rental'] as OfferType[]).map(o => (
                <button key={o} className={chip(offerType === o)} onClick={() => { setOfferType(o); setPage(1); }} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  {OFFER_ICONS[o]} {t(`offer_${o}` as never)}
                </button>
              ))}
            </div>
          ))}
          {Sec('region', t('m_region'), region !== 'any' ? 1 : 0, (
            <div className="fbar-group-chips">
              {ALL_REGIONS.map(r => (
                <button key={r.code} className={chip(region === r.code)} onClick={() => { setRegion(r.code); setPage(1); }} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {r.code === 'any' ? t('platform_all') : <>{r.flag} <span suppressHydrationWarning>{regionName(r.code, lang)}</span></>}
                </button>
              ))}
            </div>
          ))}
          {Sec('lang', t('m_lang'), langFilter.length > 0 ? 1 : 0, (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div className="fbar-group-chips">
                {(['a', 's', 'i'] as LangType[]).map(tk => (
                  <button key={tk} className={chip(langTypes.includes(tk))}
                    onClick={() => { const nt = langTypes.includes(tk) ? langTypes.filter(x => x !== tk) : [...langTypes, tk]; setLangTypes(nt.length ? nt : ALL_LANG_TYPES); setPage(1); }}>
                    {tk === 'a' ? t('filter_lang_audio') : tk === 's' ? t('filter_lang_subs') : t('filter_lang_interface')}
                  </button>
                ))}
              </div>
              <div className="fbar-group-chips">
                {LANG_LIST.map(l => (
                  <button key={l.code} className={chip(langFilter.includes(l.code))} onClick={() => { const nf = langFilter.includes(l.code) ? langFilter.filter(x => x !== l.code) : [...langFilter, l.code]; setLangFilter(nf); if (langTypes.length === 0) setLangTypes(ALL_LANG_TYPES); setPage(1); }} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {l.flag} <span suppressHydrationWarning>{langName(l.code, lang, l.label)}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="fbar-sheet-foot">
          <button className="fbar-sheet-apply" onClick={() => setSheetOpen(false)}>
            {t('m_show')} {fmtCount(total)}
          </button>
        </div>
      </div>

      <div className="ngv2-content">
        {pageItems.length === 0 && !loading ? (
          <p className="empty-state">{t('preorder_no_results')}</p>
        ) : (
          <>
            <div ref={gridRef} className="ngp-grid">
              {visible.map((item, idx) => (
                <NgPosterCard key={item.product_id} item={item} idx={idx} offerType={offerType} region={region} priceOverride={region !== 'any' ? priceMap[item.product_id] : undefined} />
              ))}
            </div>
            <Pagination page={safePage} totalPages={totalPages} onChange={goToPage} />
          </>
        )}
      </div>
    </>
  );
}
